// ════════════════════════════════════════════════════════════
//  SkyBox.jsx — THE SKY BOX: a private floating suite at the edge of the fight
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-17: "a 'sky box' area where you and your friend can watch the
//  match perfectly but also can walk around and sit as if you're in the front row
//  both easily." Everything about where it floats and how big it is lives in
//  data/skybox.js; who is in it and what they're doing lives in online/skybox.js
//  (BOX). This file only DRAWS: the suite, you, your friends, their signs.
//
//  · The suite: an obsidian floor on its own rock keel (the stands' rock + Khao
//    glow), a glass rail low enough to never cut a seated view, six seats, a bar at
//    the back (where eat/drink will come from), two lanterns. ZERO new lights.
//  · You: walk with WASD / arrows (camera-relative), E sits you in the nearest free
//    seat and stands you back up. Seated, the camera is your eyes (BattleCamera).
//  · Everyone: their own body (Lyraei / Laostre), a name over their head, a sign
//    they can hold up — and whatever they last said in the world chat goes up on it.
//  No React state from a frame: positions live in BOX and refs; React re-renders
//  only when someone arrives or leaves (useBox).
// ════════════════════════════════════════════════════════════
import { useEffect, useMemo, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { SKY, SKY_TURN, SKY_CENTER, skyBob, worldDirToLocal, seatSpot, WALK, bodyH, SIT, seatLift, EMOTES, boardOf } from "../data/skybox.js";
import { BOX, useBox, publish, signNow, myRow } from "../online/skybox.js";
import { serverNow } from "../online/ie.js";
import { rockGeo } from "./Stands.jsx";
import { drawBoard, SIGN_CELL } from "./CrowdProps.jsx";
import SkyBody from "./skyBody.jsx";
import { FrameWatch } from "../sculptGate";

const ADD = THREE.AdditiveBlending;
const DUR = Object.fromEntries(EMOTES.map((e) => [e.id, e.dur]));

// ── THE SUITE ────────────────────────────────────────────────
function roundRect(w, d, r) {
  const s = new THREE.Shape(), x = -w / 2, y = -d / 2;
  s.moveTo(x + r, y); s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + d - r); s.quadraticCurveTo(x + w, y + d, x + w - r, y + d);
  s.lineTo(x + r, y + d); s.quadraticCurveTo(x, y + d, x, y + d - r);
  s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
  return s;
}
const box = (w, h, d, x, y, z, rx = 0) => { const g = new THREE.BoxGeometry(w, h, d); if (rx) g.rotateX(rx); g.translate(x, y, z); return g; };

function Suite() {
  const parts = useMemo(() => {
    const { w, d } = SKY, T = 0.38;
    // the floor slab: a rounded slab, top at y 0
    const floor = new THREE.ExtrudeGeometry(roundRect(w, d, 0.9), { depth: T, bevelEnabled: true, bevelSize: 0.08, bevelThickness: 0.06, bevelSegments: 2, curveSegments: 10 });
    floor.rotateX(-Math.PI / 2); floor.translate(0, -T - 0.06, 0);
    // the gold inlay just inside the edge
    const inlayS = roundRect(w - 0.7, d - 0.7, 0.6); inlayS.holes.push(roundRect(w - 0.86, d - 0.86, 0.52));
    const inlay = new THREE.ShapeGeometry(inlayS, 10); inlay.rotateX(-Math.PI / 2); inlay.translate(0, 0.012, 0);
    // the walkway carpet behind the seats
    const carpet = new THREE.ShapeGeometry(roundRect(w - 2.2, 1.3, 0.4), 6); carpet.rotateX(-Math.PI / 2); carpet.translate(0, 0.008, SKY.seatZ - 1.35);
    // glass: front rail, sides, a lower back rail
    const R = SKY.rail, RF = SKY.railFront, ed = d / 2 - 0.14, ex = w / 2 - 0.14;
    const glass = mergeGeometries([box(w - 1.6, RF, 0.04, 0, RF / 2, ed), box(0.04, R, d - 1.6, -ex, R / 2, 0), box(0.04, R, d - 1.6, ex, R / 2, 0), box(w - 1.6, R * 0.7, 0.04, 0, R * 0.35, -ed)]);
    // gold: the rail's top bar, posts, seat trims, the bar's edge
    const golds = [box(w - 1.5, 0.03, 0.05, 0, RF, ed), box(0.09, 0.05, d - 1.5, -ex, R, 0), box(0.09, 0.05, d - 1.5, ex, R, 0), box(w - 1.5, 0.05, 0.09, 0, R * 0.7, -ed)];
    for (const [px, pz] of [[-ex, -ed], [ex, -ed]]) golds.push(box(0.1, R + 0.06, 0.1, px, (R + 0.06) / 2, pz));
    for (const px of [-ex, ex]) golds.push(box(0.1, R + 0.06, 0.1, px, (R + 0.06) / 2, ed));   // the front corners (the side rails end on them)
    // the seats: plush in violet, a gold line along each back
    const plush = [];
    for (let i = 0; i < SKY.seats; i++) {
      const p = seatSpot(i), H = SKY.seatH;
      plush.push(box(0.9, H - 0.16, 0.66, p.x, (H - 0.16) / 2, p.z));               // the base
      plush.push(box(1.0, 0.17, 0.82, p.x, H - 0.085, p.z));                        // the cushion
      plush.push(box(1.0, 0.78, 0.16, p.x, H + 0.34, p.z - 0.42, -0.14));           // the back, leaning
      plush.push(box(0.14, 0.26, 0.74, p.x - 0.52, H + 0.1, p.z), box(0.14, 0.26, 0.74, p.x + 0.52, H + 0.1, p.z));   // arms
      golds.push(box(1.02, 0.05, 0.05, p.x, H + 0.74, p.z - 0.5));
    }
    // the bar in the back-left corner (where eat and drink will come from) — out of the camera's way, and the lantern posts
    const bz = -d / 2 + 0.7, bx = -w / 2 + 2.1;
    plush.push(box(2.6, 0.95, 0.55, bx, 0.475, bz));
    golds.push(box(2.66, 0.04, 0.6, bx, 0.97, bz));
    for (const sx of [-1, 1]) golds.push(box(0.08, 2.1, 0.08, sx * (w / 2 - 0.6), 1.05, -d / 2 + 0.6));
    return {
      floor, inlay, carpet, glass: glass, gold: mergeGeometries(golds), plush: mergeGeometries(plush),
      keel: rockGeo(4242, 10), lamps: [-1, 1].map((sx) => [sx * (w / 2 - 0.6), 2.22, -d / 2 + 0.6]), barTop: [bx, 0.99, bz],
    };
  }, []);
  const mats = useMemo(() => ({
    floor: new THREE.MeshStandardMaterial({ color: "#151021", roughness: 0.35, metalness: 0.3, emissive: "#2a1648", emissiveIntensity: 0.32 }),
    inlay: new THREE.MeshBasicMaterial({ color: "#F2C14E", transparent: true, opacity: 0.75, toneMapped: false }),
    carpet: new THREE.MeshStandardMaterial({ color: "#3a1d5c", roughness: 0.95, emissive: "#24103d", emissiveIntensity: 0.4 }),
    glass: new THREE.MeshStandardMaterial({ color: "#a8e6ff", roughness: 0.05, metalness: 0.1, transparent: true, opacity: 0.13, depthWrite: false, emissive: "#7CE7FF", emissiveIntensity: 0.08, side: THREE.DoubleSide }),
    gold: new THREE.MeshStandardMaterial({ color: "#e8b84b", roughness: 0.3, metalness: 0.85, emissive: "#a8741c", emissiveIntensity: 0.35 }),
    plush: new THREE.MeshStandardMaterial({ color: "#3b2266", roughness: 0.75, emissive: "#1d0d36", emissiveIntensity: 0.45 }),
    rock: new THREE.MeshStandardMaterial({ color: "#221830", roughness: 0.95, metalness: 0.04, emissive: "#2a1648", emissiveIntensity: 0.4, flatShading: true }),
    glow: new THREE.MeshBasicMaterial({ color: "#7CE7FF", transparent: true, opacity: 0.26, side: THREE.BackSide, depthWrite: false, blending: ADD, toneMapped: false }),
    lamp: new THREE.MeshBasicMaterial({ color: "#ffe2a0", toneMapped: false }),
    halo: new THREE.MeshBasicMaterial({ color: "#ffcf6e", transparent: true, opacity: 0.22, depthWrite: false, blending: ADD, toneMapped: false }),
    barGlow: new THREE.MeshBasicMaterial({ color: "#7CE7FF", transparent: true, opacity: 0.5, depthWrite: false, blending: ADD, toneMapped: false }),
  }), []);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    mats.glow.opacity = 0.26 + Math.sin(t * 0.6) * 0.07;
    mats.halo.opacity = 0.2 + Math.sin(t * 1.7) * 0.04;
  });
  return (
    <group>
      <mesh geometry={parts.floor} material={mats.floor} />
      <mesh geometry={parts.inlay} material={mats.inlay} />
      <mesh geometry={parts.carpet} material={mats.carpet} />
      <mesh geometry={parts.plush} material={mats.plush} />
      <mesh geometry={parts.gold} material={mats.gold} />
      <mesh geometry={parts.glass} material={mats.glass} renderOrder={2} />
      {/* the keel: the stands' own rock, hanging under the floor, leaking Khao */}
      <group position={[0, -0.42, 0]} scale={[SKY.w * 0.42, 5.2, SKY.d * 0.46]}>
        <mesh geometry={parts.keel} material={mats.rock} />
        <mesh geometry={parts.keel} material={mats.glow} scale={[1.07, 1.04, 1.07]} />
      </group>
      {parts.lamps.map((p, i) => (
        <group key={i} position={p}>
          <mesh material={mats.lamp}><sphereGeometry args={[0.14, 14, 10]} /></mesh>
          <mesh material={mats.halo}><sphereGeometry args={[0.42, 14, 10]} /></mesh>
        </group>
      ))}
      <mesh position={[parts.barTop[0], parts.barTop[1] + 0.01, parts.barTop[2] + 0.31]} material={mats.barGlow}><boxGeometry args={[2.66, 0.02, 0.02]} /></mesh>
    </group>
  );
}

// ── LABELS: a name over a head (a canvas sprite — never an Html label in a 3D scene) ──
function useNameTex(name, mine) {
  const tex = useMemo(() => {
    const cv = document.createElement("canvas"); cv.width = 512; cv.height = 128;
    const ctx = cv.getContext("2d");
    ctx.font = '64px "Bebas Neue","Oswald","Impact",sans-serif';
    const text = String(name || "").toUpperCase(), tw = Math.min(480, ctx.measureText(text).width + 48);
    ctx.fillStyle = "rgba(12,8,22,0.78)"; ctx.strokeStyle = mine ? "#7CE7FF" : "#F2C14E"; ctx.lineWidth = 4;
    ctx.beginPath(); ctx.roundRect(256 - tw / 2, 22, tw, 84, 18); ctx.fill(); ctx.stroke();
    ctx.fillStyle = mine ? "#cfffff" : "#ffe6a6"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(text, 256, 68, 450);
    const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }, [name, mine]);
  useEffect(() => () => tex.dispose(), [tex]);
  return tex;
}

// ── A HELD SIGN: the crowd's own board (drawBoard), facing whoever is looking ──
const SIGN_W = 1.35, SIGN_H = SIGN_W * (SIGN_CELL.h / SIGN_CELL.w);
// `mine`: your own sign is a little see-through, so holding it up never blocks YOUR view of the fight
function HeldSign({ read, h, mine = false }) {
  const mesh = useRef(), up = useRef(0), drawn = useRef("");
  const { camera } = useThree();
  const art = useMemo(() => {
    const cv = document.createElement("canvas"); cv.width = SIGN_CELL.w; cv.height = SIGN_CELL.h;
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4;
    return { cv, ctx: cv.getContext("2d"), tex };
  }, []);
  const wp = useMemo(() => new THREE.Vector3(), []);
  useEffect(() => () => art.tex.dispose(), [art]);
  useFrame((_, dtRaw) => {
    const m = mesh.current; if (!m) return;
    const dt = Math.min(dtRaw || 0.016, 0.05);
    const s = read();   // { text, board, chat } or null
    up.current += ((s ? 1 : 0) - up.current) * Math.min(1, dt * 8);
    if (s) {
      const key = `${s.board}|${s.text}`;
      if (key !== drawn.current) { drawn.current = key; drawBoard(art.ctx, 0, 0, s.text, boardOf(s.board).colors); art.tex.needsUpdate = true; }
    }
    m.visible = up.current > 0.02;
    if (!m.visible) return;
    m.scale.set(1, up.current, 1);
    m.position.y = h + 0.05 + (SIGN_H / 2) * up.current + Math.sin(performance.now() / 380) * 0.025;
    // turn to face the camera (round the vertical only), in the box's own frame
    m.getWorldPosition(wp);
    m.rotation.y = Math.atan2(camera.position.x - wp.x, camera.position.z - wp.z) - SKY_TURN;
  });
  return (
    <mesh ref={mesh} visible={false} renderOrder={3}>
      <planeGeometry args={[SIGN_W, SIGN_H]} />
      <meshBasicMaterial map={art.tex} transparent opacity={mine ? 0.62 : 1} alphaTest={0.5} depthWrite={!mine} side={THREE.DoubleSide} toneMapped={false} />
    </mesh>
  );
}

// ── ONE PERSON: body + name + sign + the ring at their feet ──
function Person({ who, read, mine = false, hideBody }) {
  // who: () => { x, z, yaw, moving, seat, body, name, emote, emoteAt, sign } — read every frame
  const g = useRef(), bodyG = useRef(), plate = useRef(), ring = useRef();
  const ctl = useMemo(() => ({ facing: { x: 0, y: 1 }, speed: 0 }), []);
  const look = useRef({ seat: false, sign: false, emote: null });
  const first = who();
  const [body, setBody] = useState(first.body || "f");
  const nameTex = useNameTex(first.name, mine);
  const h = bodyH(body);
  useFrame(({ clock }) => {
    const p = who(); if (!g.current) return;
    if ((p.body || "f") !== body) setBody(p.body || "f");   // a swapped body (rare): one re-render
    const seated = p.seat >= 0;
    const at = seated ? seatSpot(p.seat) : p;
    g.current.position.set(at.x, 0, at.z + (seated ? SIT.back : 0));
    const yaw = seated ? 0 : p.yaw;
    ctl.facing.x = Math.sin(yaw); ctl.facing.y = Math.cos(yaw); ctl.speed = !seated && p.moving ? 1 : 0;
    const lift = seated ? seatLift(h) : 0;
    if (bodyG.current) { bodyG.current.position.y = lift; bodyG.current.visible = !(hideBody && hideBody()); }
    const now = serverNow();
    const em = p.emote && now - (p.emoteAt || 0) < (DUR[p.emote] || 0) * 1000 ? p.emote : null;
    const sign = read();
    look.current.seat = seated; look.current.emote = em; look.current.sign = !!sign;
    if (plate.current) { plate.current.position.y = lift + h + (sign ? SIGN_H + 0.25 : 0.32); plate.current.visible = !mine; }
    if (ring.current) { ring.current.material.opacity = (sign && sign.chat ? 0.55 : 0.32) + Math.sin(clock.elapsedTime * 2 + (mine ? 0 : 1.3)) * 0.06; ring.current.visible = !seated; }
  });
  return (
    <group ref={g}>
      <group ref={bodyG}>
        <SkyBody body={body} ctl={ctl} look={look} />
        <HeldSign read={read} h={h} mine={mine} />
      </group>
      <sprite ref={plate} scale={[1.7, 0.425, 1]} renderOrder={4}>
        <spriteMaterial map={nameTex} transparent depthWrite={false} toneMapped={false} />
      </sprite>
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <ringGeometry args={[0.42, 0.55, 32]} />
        <meshBasicMaterial color={mine ? "#7CE7FF" : "#F2C14E"} transparent opacity={0.32} depthWrite={false} blending={ADD} toneMapped={false} />
      </mesh>
    </group>
  );
}

// ── YOU: the keys move you, camera-relative ──
const KEY = { fwd: ["w", "arrowup"], back: ["s", "arrowdown"], left: ["a", "arrowleft"], right: ["d", "arrowright"] };
const held = (list) => list.some((k) => BOX.keys.has(k));
function Me({ userCam }) {
  const who = useMemo(() => () => ({ ...BOX.me, name: BOX.name }), []);
  const read = useMemo(() => () => { const r = signNow(myRow(), serverNow()); return r ? { ...r, board: BOX.me.board } : null; }, []);
  const hide = useMemo(() => () => BOX.me.seat >= 0 && userCam.current.view === "sky", [userCam]);   // seated and looking through your own eyes, your body would fill the view; from any other camera it is there to see
  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05), m = BOX.me;
    if (m.seat < 0) {
      const f = (held(KEY.fwd) ? 1 : 0) - (held(KEY.back) ? 1 : 0), r = (held(KEY.right) ? 1 : 0) - (held(KEY.left) ? 1 : 0);
      if (f || r) {
        const yaw = userCam.current.skyYaw || 0;              // the camera's offset direction (world)
        const fw = { x: -Math.sin(yaw), z: -Math.cos(yaw) };   // where the camera looks
        const rt = { x: -fw.z, z: fw.x };
        let wx = fw.x * f + rt.x * r, wz = fw.z * f + rt.z * r;
        const n = Math.hypot(wx, wz) || 1; wx /= n; wz /= n;
        const d = worldDirToLocal(wx, wz);
        m.x = THREE.MathUtils.clamp(m.x + d.x * SKY.walk * dt, -WALK.x, WALK.x);
        m.z = THREE.MathUtils.clamp(m.z + d.z * SKY.walk * dt, WALK.zMin, WALK.zMax);
        let want = Math.atan2(d.x, d.z), dd = want - m.yaw;
        while (dd > Math.PI) dd -= 2 * Math.PI; while (dd < -Math.PI) dd += 2 * Math.PI;
        m.yaw += dd * Math.min(1, dt * 10);
        m.moving = true;
      } else m.moving = false;
    } else m.moving = false;
    publish();
  });
  return <Person who={who} read={read} mine hideBody={hide} />;
}

// ── A FRIEND: eased toward where their row says they are ──
function Friend({ uid }) {
  const who = useMemo(() => () => {
    const f = BOX.folk[uid];
    if (!f) return { x: 0, z: 0, yaw: 0, seat: -1, body: "f", name: "" };
    return { x: f.cx, z: f.cz, yaw: f.cyaw, moving: f.mv, seat: f.s, body: f.b, name: f.n, emote: f.e, emoteAt: f.et };
  }, [uid]);
  const read = useMemo(() => () => { const f = BOX.folk[uid]; if (!f) return null; const r = signNow(f, serverNow()); return r ? { ...r, board: f.gb } : null; }, [uid]);
  useFrame((_, dtRaw) => {
    const f = BOX.folk[uid]; if (!f) return;
    const dt = Math.min(dtRaw || 0.016, 0.05), k = Math.min(1, dt * 8);
    const dx = f.x - f.cx, dz = f.z - f.cz;
    f.mv = Math.hypot(dx, dz) > 0.04;
    f.cx += dx * k; f.cz += dz * k;
    let dd = (f.yaw || 0) - f.cyaw; while (dd > Math.PI) dd -= 2 * Math.PI; while (dd < -Math.PI) dd += 2 * Math.PI;
    f.cyaw += dd * k;
  });
  const first = BOX.folk[uid];
  return <Person key={(first && first.n) || uid} who={who} read={read} />;
}

// ── THE WHOLE BOX ────────────────────────────────────────────
export default function SkyBox({ userCam }) {
  useBox();   // re-render when someone arrives / leaves, or I step in / out
  const root = useRef();
  useFrame(({ clock }) => { if (root.current) root.current.position.y = SKY.y + skyBob(clock.elapsedTime); });
  const folk = Object.keys(BOX.folk);
  return (
    <group ref={root} position={[SKY_CENTER.x, SKY.y, SKY_CENTER.z]} rotation={[0, SKY_TURN, 0]}>
      <Suite />
      {BOX.inside && <Me userCam={userCam} />}
      {folk.map((uid) => <Friend key={uid} uid={uid} />)}
      {/* the fail-safe watch only runs while there are bodies to protect (a plain watch never trips it) */}
      {(BOX.inside || folk.length > 0) && <FrameWatch />}
    </group>
  );
}
